import { Infinity as InfinityIcon, MessageSquare, PawPrint, Sparkles } from "lucide-react"
import { AskDynamoChatIllustration } from "~/components/AskDynamoChatIllustration"

interface UnlimitedAskDynamoCarouselCardProps {
  dogName: string
}

const EXAMPLE_QUESTIONS = (dogName: string) => [
  `Why does ${dogName} bark at strangers?`,
  "How can I improve recall?",
  `Why does ${dogName} follow me everywhere?`,
]

const FEATURES = [
  { icon: InfinityIcon, label: "Unlimited questions" },
  { icon: MessageSquare, label: "Longer conversations" },
  { icon: Sparkles, label: "Deeper guidance" },
] as const

export function UnlimitedAskDynamoCarouselCard({ dogName }: UnlimitedAskDynamoCarouselCardProps) {
  return (
    <article className="w-full min-w-full shrink-0 snap-start rounded-xl border border-[var(--color-ask-dynamo)]/25 bg-[var(--color-ask-dynamo-dashboard-bg)] p-4 shadow-[0_4px_20px_rgba(108,61,242,0.08)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-ask-dynamo)]">
              <PawPrint size={18} className="text-white" aria-hidden />
              <Sparkles
                size={10}
                className="absolute -right-0.5 -top-0.5 text-[var(--color-ask-dynamo-surface)]"
                aria-hidden
              />
            </span>
            <h2 className="text-lg font-semibold text-[var(--color-ask-dynamo-muted)]">
              Unlimited Ask Dynamo
            </h2>
            <span className="rounded-full bg-[var(--color-ask-dynamo)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
              New
            </span>
          </div>

          <p className="mt-3 text-sm font-medium text-[var(--color-text-primary)]">
            Get unlimited personalised guidance for {dogName}.
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Ask questions about behaviour, training, routines and everyday situations.
          </p>

          <ul className="mt-3 space-y-1 text-sm text-[var(--color-ask-dynamo-muted)]">
            {EXAMPLE_QUESTIONS(dogName).map((question) => (
              <li key={question} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-ask-dynamo)]" aria-hidden />
                <span>{question}</span>
              </li>
            ))}
          </ul>
        </div>

        <AskDynamoChatIllustration className="hidden h-16 w-20 shrink-0 sm:block" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-[var(--color-ask-dynamo)]/15 pt-3">
        {FEATURES.map(({ icon: Icon, label }) => (
          <div key={label} className="flex flex-col items-center gap-1 text-center">
            <Icon size={16} className="text-[var(--color-ask-dynamo)]" aria-hidden />
            <span className="text-[10px] leading-tight font-medium text-[var(--color-ask-dynamo-muted)]">
              {label}
            </span>
          </div>
        ))}
      </div>
    </article>
  )
}
